import { useState } from 'react';
import { Box, HStack, Spacer, VStack, View } from 'native-base';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';

import { CheckDone01Icon, Button, SearchField, Text, Modal } from '@pimm/base';
import { useSiteConfig } from '@app/features/store-core';
import { LearningHub, useGetHubStats } from '@app/features/app-insights';
import { useAuthPermissions } from '@app/features/app';
import { EditStatus } from '@app/features/app-insights/components/edit-status';
import { EditStatusConfirmation } from '@app/features/app-insights/components/edit-status-confirmation';
import { useModalFocus } from '@app/hooks/modal-focus.hook';

type LearningHubPanelProps = {};

export const LearningHubPanel = ({}: LearningHubPanelProps) => {
  const { siteConfig } = useSiteConfig();
  const { t } = useTranslation();
  const queryHubStats = useGetHubStats(siteConfig?.id);
  const permissions = useAuthPermissions(state => state.permissions);
  const modalConfirm = useModalFocus();
  const [searchValue, setSearchValue] = useState<string>('');

  const isAuthorized = !!permissions?.learningHubLearner?.write;

  return (
    <VStack h="full">
      <HStack space={2} alignItems="center" py={2} px={4} minH="58px" borderBottomWidth={1}>
        <Box>
          <HStack space={2} alignItems="center" h={6}>
            <Text size="xl" fontWeight={600} color="black" lineHeight="xs">
              {t('app:team_members')}
            </Text>
            {!isEmpty(queryHubStats.data) && (
              <Box rounded="full" justifyContent="center" py={1} px={2} borderColor="primary.500" borderWidth={1} bg="primary.50">
                <Text size="sm" fontWeight={600} color="primary.700" lineHeight="xs">
                  {`${queryHubStats.data?.length ?? 0} ${t('app:users')}`}
                </Text>
              </Box>
            )}
          </HStack>
          <Text size="md" fontWeight={400} color="gray.600" lineHeight="xs">
            {t('app:team_members_training_progress')}
          </Text>
        </Box>

        <Spacer />

        <SearchField
          minWidth={300}
          placeholder={t('app:search_user')}
          value={searchValue}
          onChangeText={setSearchValue}
          onClear={() => setSearchValue('')}
        />

        <Button onPress={modalConfirm.setOpen} endIcon={<CheckDone01Icon size="sm" color="black" />}>
          {t('app_insights:edit_status')}
        </Button>
      </HStack>

      <View flex={1}>
        <LearningHub key={siteConfig?.id} queryHubStats={queryHubStats} search={searchValue?.trim()?.toLowerCase()} />
      </View>

      <Modal
        isOpen={modalConfirm.isOpen}
        onClose={modalConfirm.setHide}
        size={isAuthorized ? 'xl' : 'md'}
        _content={{ rounded: '2xl', maxW: isAuthorized ? 960 : 400 }}
        noPadding
      >
        {isAuthorized ? <EditStatus isAuthorized queryHubStats={queryHubStats} /> : <EditStatusConfirmation />}
      </Modal>
    </VStack>
  );
};
